import { parseFullName, getSignUpProvider } from '@/services/user';
import { getBrowserData } from '@/services/browser';
import { getExtensionDetails } from '@/services/extension';
import { SIGN_UP_METHODS } from '@/constants';
import router from '@/router';
import { timestamp } from '../datetime';

function getSignUpMethodByProviderId(providerId) {
  switch (providerId) {
    case 'password':
      return SIGN_UP_METHODS.EMAIL;
    case 'google.com':
      return SIGN_UP_METHODS.GOOGLE;
    case 'facebook.com':
      return SIGN_UP_METHODS.FACEBOOK;
    default:
      return SIGN_UP_METHODS.EMAIL;
  }
}

export function getAdditionalDataAccordingToRoute() {
  const { target, ...params } = router?.currentRoute?.query || {};

  const embeddedParams = Object.entries(params).reduce((results, [key, value]) => {
    if (!value) {
      return results;
    }

    const hasUrlAsAKey = key.indexOf('http://') === 0 || key.indexOf('https://') === 0;
    const transformedKey = hasUrlAsAKey ? key.split('?')[1]?.split('&')[0] : key;

    if (!key) {
      return results;
    }

    return Object.assign(results, { [transformedKey]: value });
  }, {});

  if (!target) {
    return embeddedParams;
  }

  return {
    [target]: embeddedParams,
  };
}

export function getDefaultPrivateInfo(options = {}) {
  const { forceSet } = options;

  return {
    allowBrowsingHistory: forceSet ?? false,
    allowRetargeting: forceSet ?? false,
    allowDigitalFingerprint: forceSet ?? false,
    allowOpenMarketTargeting: forceSet ?? true,
    allowPersonalisedAds: forceSet ?? true,
  };
}

function getFirstName(signUpResults) {
  /* eslint-disable camelcase */
  return (
    signUpResults?.additionalUserInfo?.profile?.given_name ||
    parseFullName(signUpResults?.additionalUserInfo?.profile?.name).firstName ||
    signUpResults?.originUserData?.firstName ||
    parseFullName(signUpResults?.originUserData?.displayName).firstName
  );
  /* eslint-enable camelcase */
}

function getLastName(signUpResults) {
  /* eslint-disable camelcase */
  return (
    signUpResults?.additionalUserInfo?.profile?.family_name ||
    parseFullName(signUpResults?.additionalUserInfo?.profile?.name).lastName ||
    signUpResults?.originUserData?.lastName ||
    parseFullName(signUpResults?.originUserData?.displayName).lastName
  );
  /* eslint-enable camelcase */
}

function getEmail(signUpResults) {
  return signUpResults?.user?.email || signUpResults?.originUserData?.email;
}

function getEmailVerified(signUpResults) {
  return signUpResults?.user?.emailVerified ?? signUpResults?.originUserData?.emailVerified;
}

function getSignUpMethod(signUpResults) {
  return getSignUpMethodByProviderId(getSignUpProvider(signUpResults?.user));
}

function getPhotoUrl(signUpResults) {
  return signUpResults?.user?.photoURL || null;
}

function getReferringPage() {
  return document.referrer || window.location.href;
}

export function getUserMetadata(user) {
  const meta = user?.metadata;
  if (!meta) {
    return {};
  }

  return {
    createdAt: timestamp(meta.creationTime),
    lastSignedInAt: timestamp(meta.lastSignInTime),
  };
}

function getUserData(signUpResults) {
  return {
    firstName: getFirstName(signUpResults),
    lastName: getLastName(signUpResults),
    email: getEmail(signUpResults),
    emailVerified: getEmailVerified(signUpResults),
    photoUrl: getPhotoUrl(signUpResults),
    signUpMethod: getSignUpMethod(signUpResults),
  };
}

export function getPrivateInfoForSignUp(signUpResults) {
  return {
    ...getUserData(signUpResults),
    ...getUserMetadata(signUpResults?.user),
    ...getDefaultPrivateInfo(),
    browserUsedToRegister: getBrowserData(),
    ...getAdditionalDataAccordingToRoute(),
    ...signUpResults?.queryData,
    referringPage: getReferringPage(),
  };
}

export async function getExtensionDataForPrivateInfo() {
  const extensionDetails = await getExtensionDetails();

  if (!extensionDetails.installed) {
    return {
      extensionInstalled: false,
    };
  }

  return {
    extensionInstalled: extensionDetails.installed,
    extensionVersion: extensionDetails.version,
    extensionType: extensionDetails.type,
  };
}
